import { ConnectError } from "@connectrpc/connect";
import { captureException } from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import type {
  Store,
  TransactionGroup,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { PayResponse } from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import ProgressActivity from "~icons/material-symbols/progress-activity";
import { css } from "../../../styled-system/css";
import Header from "../../components/header";
import { formatDate } from "../../libs/date";
import { t } from "../../libs/i18n";
import { formatNumber } from "../../libs/utils";
import PointUsageBlock from "./components/point-usage-block";
import StoreBlock from "./components/store-block";
import type { PayForm } from "./use-pay-form";

type PayConfirmProps = {
  store: Store;
  payForm: PayForm;
  onBack: () => void;
  onComplete: (transactionGroup: TransactionGroup) => void;
  pay: () => Promise<PayResponse>;
};

export default function PayConfirm({
  store,
  payForm,
  onBack,
  pay,
  onComplete,
}: PayConfirmProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const upperStoreRef = useRef<HTMLDivElement>(null);
  const [upperStoreHeight, setUpperStoreHeight] = useState(48);
  const [currentTime, setCurrentTime] = useState(
    formatDate(new Date(), "dateTimeSeconds"),
  );

  useEffect(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    const element = upperStoreRef.current;
    if (!element) return;

    const updateHeight = () =>
      setUpperStoreHeight(element.getBoundingClientRect().height);
    updateHeight();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(formatDate(new Date(), "dateTimeSeconds"));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handlePay = useCallback(() => {
    setLoading(true);
    pay()
      .then((response) => {
        if (response.transactionGroup) {
          onComplete(response.transactionGroup);
        } else {
          throw new Error(t("トランザクショングループがありません"));
        }
      })
      .catch((error) => {
        if (error instanceof ConnectError) {
          toast.error(t("支払いに失敗しました \n[CONNECT_PAY_FAILED]"));
        } else if (error instanceof Error) {
          toast.error(`${t("支払いに失敗しました")} \n[${error.message}]`);
        } else {
          toast.error(t("支払いに失敗しました"));
        }
        captureException(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pay, onComplete]);

  return (
    <div
      className={css({
        bg: "#3D4756",
      })}
    >
      <Header title="" theme="dark" onClickBack={onBack} />
      <div
        className={css({
          px: "16px",
          pt: "100px",
          display: "flex",
          flexDirection: "column",
          transform: "rotate(180deg)",
          color: "white",
          marginTop: "-1px",
        })}
      >
        <div ref={upperStoreRef}>
          <StoreBlock store={store} reverse size="large" />
        </div>

        <div
          className={css({
            fontSize: "20px",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: 0,
          })}
        >
          <div
            className={css({
              fontSize: "48px",
              textAlign: "center",
              position: "relative",
            })}
          >
            <span>{formatNumber(payForm.payAmount)}</span>

            <div
              className={css({
                position: "absolute",
                fontSize: "16px",
                bottom: "10px",
                right: "-22px",
              })}
            >
              pt
            </div>
          </div>
        </div>

        <div
          className={css({
            display: "flex",
            justifyContent: "center",
          })}
        >
          {currentTime}
        </div>
      </div>

      <div
        style={{
          top: isOpen ? `${200 + Math.max(0, upperStoreHeight - 48)}px` : 0,
        }}
        className={css({
          p: "16px",
          pb: "64px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          bg: "background.background",
          roundedTop: "2xl",
          position: "absolute",
          left: 0,
          right: 0,
          transition: "top 0.8s ease-in-out",
        })}
      >
        <StoreBlock store={store} size="large" />
        <div
          className={css({
            bg: "white",
            rounded: "xl",
            p: "24px",
          })}
        >
          <div
            className={css({
              fontSize: "20px",
              borderBottom: "1px solid",
              borderColor: "border.primary",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: 0,
            })}
          >
            <div
              className={css({
                fontSize: "48px",
                color: "text.primary",
                textAlign: "center",
                position: "relative",
              })}
            >
              <span>{formatNumber(payForm.payAmount)}</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handlePay}
          className={css({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bg: "surface.accentPrimary",
            color: "white",
            rounded: "xl",
            px: "32px",
            py: "12px",
            fontSize: "20px",
            fontWeight: "600",
            "&:disabled": {
              bg: "surface.accentDisable",
            },
          })}
          disabled={loading}
        >
          {loading ? (
            <ProgressActivity
              className={css({
                animation: "spin 1s linear infinite",
              })}
            />
          ) : (
            t("支払う")
          )}
        </button>
        <button
          type="button"
          onClick={onBack}
          className={css({
            mt: "16px",
            mb: "16px",
            fontWeight: "600",
          })}
          disabled={loading}
        >
          {t("もどる")}
        </button>

        <PointUsageBlock balances={payForm.balances} />
      </div>
    </div>
  );
}
